import React, { useEffect, useRef, useCallback } from 'preact/compat';
import classNames from 'classnames';
import { decodeHTML } from '@escid-espp1/helpers';
import styles from './dataSheetButton.module.scss';
import DataSheetIcon from '../../assets/datasheet_icon';
import ModalWrapper from '../modalWrapper/modalWrapper';
import { Fetch } from '../Fetch';
import type DrsProps from '../../types/drsProps';
import type { IVariantChangePayload, IApiResponse } from '../../types/drsProps';

function LinkButtonContent({ label }: { label: string; }) {
    return (
        <div className={styles.link_title}>
            <span className={styles.link_icon}>
                <DataSheetIcon />
            </span>
            <span className={styles.link_label}>{label}</span>
        </div>
    );
}

const LinkButton = ({ label, href, extraStyles }: { label: string; href: string; extraStyles?: string[] }) => {

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            window.open(href, '_blank', 'noreferrer');
        }
    };

    const handleClick = () => {
        window.open(href, '_blank', 'noreferrer');
    };
    
    const additionalStyles = Array.isArray(extraStyles) ? extraStyles : []

    return (
        <div
            class={classNames(styles.link_button, ...additionalStyles)}
            data-testid='datasheet_link'
            data-semantic-id='datasheet_link'
            role="button"
            target='_blank'
            rel='noreferrer'
            focusable
            onClick={handleClick}
            onKeyDown={handleKeyDown}
        >
            <LinkButtonContent label={label} />
        </div>
    );
}

function DataSheetButton({ articleDataSheets, conformityFile, salesDesignation, l10n, applicationName, apiLink }: DrsProps) {
    const [datasheetsModalOpen, setDatasheetsModalOpen] = React.useState(false);
    const [dataSheetState, setdataSheetState] = React.useState({
        conformityFile,
        salesDesignation,
        articleDataSheets
    });
    const buttonRef = useRef<HTMLDivElement>(null);
    const fetch = new Fetch(apiLink);

    const handleCloseWithKeyboard = () => {
        setDatasheetsModalOpen(false);
        globalThis.shell.tabNav.focus(buttonRef.current as Element, 'keyboard');
    }

    useEffect(() => {
        if (!datasheetsModalOpen) {
            return;
        }

        const scrollY = window.scrollY;
        const pageContainer =
            document.querySelector('.page-container') ||
            document.querySelector('.container');

        if (pageContainer instanceof HTMLElement) {
            pageContainer.style.overflow = 'hidden';
            pageContainer.style.touchAction = 'none';
        }

        document.body.classList.add('drs-scroll-lock');
        document.body.style.top = `-${scrollY}px`;

        return () => {
            if (pageContainer instanceof HTMLElement) {
                pageContainer.style.overflow = '';
                pageContainer.style.touchAction = '';
            }

            document.body.classList.remove('drs-scroll-lock');
            document.body.style.top = '';
            window.scrollTo(0, scrollY);
        };
    }, [datasheetsModalOpen]);

    useEffect(() => {
        if (window.shell) {
            // bind for pdp variant change
            if (applicationName === 'pdp') {
                window.shell.subscribeTo('ESPP1.Detailpage.VariantChange.Public', (payload: IVariantChangePayload) => {
                    fetch.fetch(payload.salesArticleNo, payload.colorCode.toString(), payload.masterArticleNo, applicationName).then(
                        (response: IApiResponse | null) => {
                            if (response) {
                                setdataSheetState(response);
                            }
                        }
                    );
                }, 'ESPP.DatasheetRetrievalService');
            }
        }
    }, []);
    
    const handleOpenModalClick = useCallback(() => {
        setDatasheetsModalOpen(true);
    }, []);

    const showConformity = !!dataSheetState.conformityFile?.path;
    const showArticleDataSheets = Array.isArray(dataSheetState.articleDataSheets) && dataSheetState.articleDataSheets.length > 0;

    if (!showArticleDataSheets && !showConformity) {
        return null;
    }

    // Build a unified list of all available PDFs, independent of source
    const allPdfs: { name: string; path: string }[] = [];

    if (showConformity && dataSheetState.conformityFile?.path) {
        allPdfs.push({
            name: decodeHTML(l10n.declarationOfComformity),
            path: dataSheetState.conformityFile.path,
        });
    }

    if (showArticleDataSheets) {
        dataSheetState.articleDataSheets?.forEach((datasheet) => {
            allPdfs.push({ name: datasheet.name, path: datasheet.path });
        });
    }

    const numberOfFiles = allPdfs.length;
    const buttonLocalizationKey = decodeHTML(numberOfFiles > 1 ? l10n.dataSheets : l10n.dataSheet);
    const hintText = numberOfFiles > 1 ? l10n.dataSheetsButtonInfo : l10n.dataSheetButtonInfo;
    // Exactly one PDF (any source) -> open it directly, no modal
    if (numberOfFiles === 1) {
        return (
            <div className={styles.datasheet_container}>
                <div class={styles.datasheet_hint} dangerouslySetInnerHTML={{ __html: hintText }}></div>
                <LinkButton href={allPdfs[0].path} label={buttonLocalizationKey} extraStyles={[styles.modal_open_button]} />
            </div>

        );
    }

    // Two or more PDFs -> open modal
    return (
        <div className={styles.datasheet_container}>
            <div class={styles.datasheet_hint} dangerouslySetInnerHTML={{ __html: hintText }}></div>
            <div
                id="modal-open-button"
                data-testid="modal-open-button"
                class={classNames(styles.link_button, styles.modal_open_button)}
                onClick={handleOpenModalClick}
                ref={buttonRef}
                focusable
            >
                <LinkButtonContent label={buttonLocalizationKey} />
            </div>
            <ModalWrapper
                isOpen={datasheetsModalOpen}
                onClose={() => setDatasheetsModalOpen(false)}
                onKeyClose={handleCloseWithKeyboard}
                title={buttonLocalizationKey}
                subtitle={dataSheetState.salesDesignation}
                content={
                    <div className={styles.datasheet_modal_content}>
                        {allPdfs.map((pdf, index) => (
                            <LinkButton key={pdf.name + index} href={pdf.path} label={pdf.name} />
                        ))}
                    </div>
                }
            ></ModalWrapper>
        </div>
    );
}

export default DataSheetButton;